'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import StudentLoginForm from './StudentLoginForm';
import ParentLoginForm from './ParentLoginForm';
import ProfessionalLoginForm from './ProfessionalLoginForm';
import AdminLoginForm from './AdminLoginForm';
import { Users, Briefcase, Shield, GraduationCap } from 'lucide-react';

type RoleTab = 'student' | 'parent' | 'professional' | 'admin';

const tabs: { id: RoleTab; label: string; icon: React.ReactNode; desc: string }[] = [
  { id: 'student', label: 'Student', icon: <GraduationCap size={16} />, desc: 'Enter your campus' },
  { id: 'parent', label: 'Parent', icon: <Users size={16} />, desc: 'Monitor & protect' },
  { id: 'professional', label: 'Professional', icon: <Briefcase size={16} />, desc: 'Advanced learning' },
  { id: 'admin', label: 'Admin', icon: <Shield size={16} />, desc: 'Control center' },
];

export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<RoleTab>('student');
  const [isSignUp, setIsSignUp] = useState(false);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel — campus illustration */}
      <div className="hidden lg:flex flex-col flex-1 campus-hero-gradient p-12 relative overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-20 blur-3xl bg-primary pointer-events-none" />
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full opacity-10 blur-3xl bg-accent pointer-events-none" />

        <Link href="/" className="flex items-center gap-3 mb-16 relative z-10">
          <AppLogo size={40} />
          <span className="font-extrabold text-2xl text-foreground">JarvondisU</span>
        </Link>

        {/* Campus scene */}
        <div className="flex-1 flex flex-col justify-center relative z-10">
          <div className="flex justify-center mb-10">
            <svg viewBox="0 0 280 280" className="w-64 h-64 tree-sway" aria-label="Jarvondis University Tree of Life campus illustration">
              <ellipse cx="140" cy="255" rx="70" ry="10" fill="#dcfce7" />
              <path d="M136 248 Q132 200 130 165 Q128 130 136 95 Q140 78 144 95 Q152 130 150 165 Q148 200 144 248 Z" fill="#92400e" opacity="0.7" />
              <path d="M140 248 Q115 222 106 186 Q97 150 114 123 Q131 96 148 78" stroke="#f59e0b" strokeWidth="2.5" fill="none" strokeDasharray="5 3" opacity="0.6" />
              <circle cx="140" cy="82" r="62" fill="#16a34a" opacity="0.12" />
              <circle cx="140" cy="82" r="48" fill="#16a34a" opacity="0.22" />
              <circle cx="140" cy="82" r="35" fill="#16a34a" opacity="0.45" />
              <path d="M140 118 Q114 90 96 64" stroke="#166534" strokeWidth="3.5" fill="none" strokeLinecap="round" />
              <path d="M140 118 Q166 90 184 64" stroke="#166534" strokeWidth="3.5" fill="none" strokeLinecap="round" />
              <path d="M140 100 Q140 72 140 44" stroke="#166534" strokeWidth="4.5" fill="none" strokeLinecap="round" />
              <circle cx="96" cy="64" r="16" fill="#22c55e" opacity="0.8" />
              <circle cx="184" cy="64" r="16" fill="#22c55e" opacity="0.8" />
              <circle cx="140" cy="40" r="20" fill="#16a34a" />
              <circle cx="140" cy="248" r="52" fill="none" stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="7 3" opacity="0.45" className="fountain-spin" style={{ transformOrigin: '140px 248px' }} />
              <rect x="72" y="238" width="36" height="4" rx="2" fill="#78350f" />
              <rect x="76" y="228" width="3" height="12" rx="1" fill="#78350f" />
              <rect x="102" y="228" width="3" height="12" rx="1" fill="#78350f" />
              <circle cx="90" cy="222" r="6" fill="#f59e0b" />
              <path d="M86 228 Q90 240 94 228" stroke="#d97706" strokeWidth="1.5" fill="none" />
            </svg>
          </div>

          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-foreground mb-4">Welcome Back to Campus</h2>
            <p className="text-muted-foreground text-base leading-relaxed max-w-sm mx-auto">
              Your AI tutor, your courses, and your learning memory are waiting. The Tree of Life grows with every lesson.
            </p>
          </div>

          {/* Mini stats */}
          <div className="flex justify-center gap-8 mt-10">
            {[
              { val: '48K+', label: 'Learners' },
              { val: '340+', label: 'Courses' },
              { val: '4', label: 'Wings' },
            ].map((s) => (
              <div key={`auth-stat-${s.label}`} className="text-center">
                <p className="text-xl font-extrabold text-primary font-tabular">{s.val}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-sm text-muted-foreground relative z-10">
          © 2026 Jarvondis University · <Link href="/" className="hover:underline">Privacy</Link> · <Link href="/" className="hover:underline">Terms</Link>
        </p>
      </div>

      {/* Right panel — auth form */}
      <div className="flex flex-col w-full lg:max-w-lg xl:max-w-xl bg-card border-l border-border overflow-y-auto">
        <div className="flex-1 flex flex-col justify-center p-8 lg:p-12">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-extrabold text-lg text-foreground">JarvondisU</span>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-extrabold text-foreground mb-1">
              {isSignUp ? 'Create Your Account' : 'Sign In to Campus'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isSignUp ? 'Join thousands of learners on the living digital campus.' : 'Welcome back — your learning journey continues.'}
            </p>
          </div>

          {/* Role tabs */}
          <div className="grid grid-cols-4 gap-1 bg-muted rounded-xl p-1 mb-8">
            {tabs.map((tab) => (
              <button
                key={`auth-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg text-xs font-semibold transition-all duration-150 ${
                  activeTab === tab.id
                    ? 'bg-card text-foreground shadow-card'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.icon}
                <span className="hidden sm:block">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Form */}
          <div className="fade-in">
            {activeTab === 'student' && <StudentLoginForm isSignUp={isSignUp} />}
            {activeTab === 'parent' && <ParentLoginForm isSignUp={isSignUp} />}
            {activeTab === 'professional' && <ProfessionalLoginForm isSignUp={isSignUp} />}
            {activeTab === 'admin' && <AdminLoginForm />}
          </div>

          {/* Toggle sign up / sign in */}
          {activeTab !== 'admin' && (
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                {' '}
                <button
                  onClick={() => setIsSignUp((p) => !p)}
                  className="text-primary font-semibold hover:underline"
                >
                  {isSignUp ? 'Sign In' : 'Create Account'}
                </button>
              </p>
            </div>
          )}

          {/* Credentials box */}
          <div className="mt-8 p-4 bg-muted/60 rounded-xl border border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Demo Credentials</p>
            <div className="space-y-2">
              {[
                { role: 'Student', email: 'amara.osei@jarvondisu.edu', password: 'Campus@2026' },
                { role: 'Parent', email: 'david.osei@jarvondisu.edu', password: 'Parent@2026' },
                { role: 'Professional', email: 'nadia.volkov@jarvondisu.edu', password: 'ProLearner@26' },
              ].map((c) => (
                <div key={`cred-${c.role}`} className="flex items-center gap-3 text-xs">
                  <span className="w-20 font-semibold text-foreground shrink-0">{c.role}</span>
                  <span className="text-muted-foreground font-mono truncate">{c.email}</span>
                  <span className="text-muted-foreground font-mono shrink-0">{c.password}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}